import { css } from 'styled-components';

import {
  BREAKPOINT_LG,
  BREAKPOINT_MD,
  BREAKPOINT_SM,
  FONT_FAMILY_MAIN,
  FONT_SIZE_TEXT,
  INPUT_HEIGHT,
  MOCKUP_WIDTH_SM,
  TRANSITION_DURATION,
  TRANSITION_FUNCTION,
} from '../constants';
import mq from '@/utils/media-queries';
import theme from './theme';

export const fontFamilyMain = css`
font-family: "${FONT_FAMILY_MAIN}", sans-serif, Arial;
`;

export const toRelativeUnits = (value, context = BREAKPOINT_LG) => (100 * value) / context;

export const toVw = (value, context) => `${toRelativeUnits(value, context)}vw`;

export const toVwTablet = (value, context = BREAKPOINT_MD) => toVw(value, context);

export const adjustingMultiplierMobile = BREAKPOINT_SM / MOCKUP_WIDTH_SM;

export const toVwMobile = (value, context = BREAKPOINT_SM) => toVw(adjustingMultiplierMobile * value, context);

export const toEm = (value, context = FONT_SIZE_TEXT) => `${value / context}em`;

export const crossScreenFontSize = fontSize =>
  css`
    font-size: ${toVw(fontSize)};

    ${mq.tablet(css`
      font-size: ${toVwTablet(fontSize)};
    `)};

    ${mq.mobile(css`
      font-size: ${toVwMobile(fontSize)};
    `)};
  `;

export const crossScreenWidth = value =>
  css`
    width: ${toVw(value)};

    ${mq.tablet(css`
      width: ${toVwTablet(value)};
    `)};

    ${mq.mobile(css`
      width: ${toVwMobile(value)};
    `)};
  `;

export const crossScreenHeight = value =>
  css`
    height: ${toVw(value)};

    ${mq.tablet(css`
      height: ${toVwTablet(value)};
    `)};

    ${mq.mobile(css`
      height: ${toVwMobile(value)};
    `)};
  `;

export const crossScreenMarginTop = value =>
  css`
    margin-top: ${toVw(value)};

    ${mq.tablet(css`
      margin-top: ${toVwTablet(value)};
    `)};

    ${mq.mobile(css`
      margin-top: ${toVwMobile(value)};
    `)};
  `;

export const crossScreenMarginLeft = value =>
  css`
    margin-left: ${toVw(value)};

    ${mq.tablet(css`
      margin-left: ${toVwTablet(value)};
    `)};

    ${mq.mobile(css`
      margin-left: ${toVwMobile(value)};
    `)};
  `;

export const crossScreenMarginRight = value =>
  css`
    margin-right: ${toVw(value)};

    ${mq.tablet(css`
      margin-right: ${toVwTablet(value)};
    `)};

    ${mq.mobile(css`
      margin-right: ${toVwMobile(value)};
    `)};
  `;

export const crossScreenMarginBottom = value =>
  css`
    margin-bottom: ${toVw(value)};

    ${mq.tablet(css`
      margin-bottom: ${toVwTablet(value)};
    `)};

    ${mq.mobile(css`
      margin-bottom: ${toVwMobile(value)};
    `)};
  `;

export const crossScreenPaddingTop = value =>
  css`
    padding-top: ${toVw(value)};

    ${mq.tablet(css`
      padding-top: ${toVwTablet(value)};
    `)};

    ${mq.mobile(css`
      padding-top: ${toVwMobile(value)};
    `)};
  `;

export const crossScreenPaddingRight = value =>
  css`
    padding-right: ${toVw(value)};

    ${mq.tablet(css`
      padding-right: ${toVwTablet(value)};
    `)};

    ${mq.mobile(css`
      padding-right: ${toVwMobile(value)};
    `)};
  `;

export const crossScreenPaddingBottom = value =>
  css`
    padding-bottom: ${toVw(value)};

    ${mq.tablet(css`
      padding-bottom: ${toVwTablet(value)};
    `)};

    ${mq.mobile(css`
      padding-bottom: ${toVwMobile(value)};
    `)};
  `;

export const fontFace = (name, src, fontWeight = 'normal', fontStyle = 'normal') => css`
    @font-face{
        font-family: "${name}";
        src: url(${require('../fonts/' + src + '.woff')}) format("woff"),
             url(${require('../fonts/' + src + '.woff2')}) format("woff2");
        font-style: ${fontStyle};
        font-weight: ${fontWeight};
    }
`;

export const clearfix = css`
  zoom: 1;

  &:after {
    content: '';
    display: block;
    height: 0;
    clear: both;
    visibility: hidden;
    font-size: 0;
  }
`;

export const responsiveImage = css`
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
`;

const animatedProperties = ['opacity', 'fill', 'color', 'background-color'];

export const transitionDefault = css`
  transition: ${animatedProperties.map(prop => `${prop} ${TRANSITION_DURATION} ${TRANSITION_FUNCTION}`).join(',')};
`;

export const animated = css`
  ${transitionDefault};

  &:hover {
    transition: none;
  }
`;

export const hoverDefault = css`
  ${animated};

  &:hover {
    opacity: 0.7;
  }
`;

const lineThroughLink = css`
  color: ${props => props.theme.colorMinor};
  background: linear-gradient(${props => props.theme.colorMinor}, ${props => props.theme.colorMinor}) no-repeat center
    60%;
  background-size: 100% ${toEm(1.5)};
  pointer-events: none;

  &:hover {
    background-size: 100% ${toEm(1.5)};
  }
`;

export const linkChangeColor = color => css`
  color: ${color};

  &:hover {
    border-bottom-color: ${color};
  }
`;

export const link = css`
  display: inline-block;
  line-height: 0.94;
  cursor: pointer;
  text-decoration: none;
  color: ${props => props.theme.colorLink};
  border-bottom: ${toEm(1.25)} solid transparent;
  transition: border ${TRANSITION_DURATION} ${TRANSITION_FUNCTION};

  &:hover {
    border-bottom-color: ${props => props.theme.colorLink};
    transition: none;
  }

  ${props => props.color && linkChangeColor(props.color)};
  ${props => props.active && lineThroughLink};
`;

export const col = css`
  float: left;
  padding-right: ${toVw(10)};
  box-sizing: border-box;
`;

export const input = css`
  border: none;
  height: ${toVw(INPUT_HEIGHT)};
  border-bottom: 2px solid ${props => props.theme.colorInputBorder};
  background-color: transparent;
  color: ${props => props.theme.colorText};
  width: 100%;
  font-weight: 300;
  box-sizing: border-box;
  -webkit-appearance: none;
  border-radius: 0;
  ${fontFamilyMain};
  ${crossScreenFontSize(FONT_SIZE_TEXT)};

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${props => props.theme.colorText};
  }

  &::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }

  ${props =>
    props._error === 'true' &&
    css`
      border-bottom-color: #f8173e;
    `};

  ${mq.tablet(css`
    height: ${toVwTablet(INPUT_HEIGHT)};
  `)};

  ${mq.mobile(css`
    height: ${toVwMobile(INPUT_HEIGHT)};
  `)};
`;

export const verticalAlignWrapper = css`
  &:before {
    content: '';
    display: inline-block;
    vertical-align: middle;
    height: 100%;
  }
`;

export const verticalAlign = css`
  display: inline-block;
  vertical-align: middle;
`;

export const resetButton = css`
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;
  font-family: inherit;

  /* Corrects inability to style clickable \`input\` types in iOS */
  -webkit-appearance: none;

  &:focus {
    outline: none;
  }

  &::-moz-focus-inner {
    border: 0;
    padding: 0;
  }
`;

export const lineThrough = color => css`
  background: linear-gradient(${color}, ${color}) no-repeat left 60%;
  background-size: 100% ${toEm(1.5)};
  transition: background ${TRANSITION_DURATION} ${TRANSITION_FUNCTION};
  border-bottom: none;
`;

export const lineThroughHover = color => css`
  ${lineThrough(color)};
  background-size: 0 ${toEm(1.5)};

  &:hover {
    background-size: 100% ${toEm(1.5)};
    transition: background ${TRANSITION_DURATION} ${TRANSITION_FUNCTION};

    @media (hover: none) {
      background-size: 0 0;
    }
  }
`;

export const carouselCommon = (color = theme.colorAccent) => css`
  position: relative;
  overflow: hidden;

  .slick-list {
    overflow: visible;
  }

  .slick-dots {
    position: absolute;
    bottom: ${toVw(36)};
    width: 100%;
    left: 0;
    text-align: center;
    font-size: 0;

    ${mq.tablet(css`
      bottom: ${toVwTablet(36)};
    `)};

    ${mq.mobile(css`
      bottom: ${toVwMobile(24)};
    `)};

    li {
      display: inline-block;
      margin: 0 8px;

      ${mq.tablet(css`
        margin: 0 ${toVwTablet(12)};
      `)};

      ${mq.mobile(css`
        margin: 0 ${toVwMobile(12)};
      `)};

      button {
        border: none;
        margin: 0;
        padding: 0;
        background-color: ${props => (props.pagerData && props.pagerData.color) || '#d8d8d8'};
        width: 10px;
        height: 10px;
        border-radius: 50%;
        font-size: 0;
        cursor: pointer;
        ${transitionDefault};

        &:hover {
          background-color: ${color};
        }

        &:focus {
          outline: none;
        }

        ${mq.tablet(css`
          width: ${toVwTablet(10)};
          height: ${toVwTablet(10)};
        `)};

        ${mq.mobile(css`
          width: ${toVwMobile(10)};
          height: ${toVwMobile(10)};
        `)};
      }

      &.slick-active {
        button {
          background-color: ${props => (props.pagerData && props.pagerData.activeColor) || color};
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/utils/styles.js